import "./InvoiceDetailForm.scss";

import Button from "components/Button/Button";
import { ControlledTextArea, TextInput } from "components/inputs";
import ControlledMultiSelect from "components/inputs/Select/ControlledMultiSelect";
import Toggle from "components/inputs/Toggle/Toggle";
import useQuickBooksConnectionStatus from "components/Invoicing/QuickBooks/useQuickBooksConnectionStatus";
import useQuickBooksReconnect from "components/Invoicing/QuickBooks/useQuickBooksReconnect";
import { useInvoice } from "components/Invoicing/useInvoice";
import SectionBreak from "components/SectionBreak";
import Tooltip from "components/Tooltip/Tooltip";
import { format, parse } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetClientByIdQuery } from "services/client-api";
import { SERVICE_TYPE } from "types/DataTypes";
import { DATE_FORMAT } from "utils/date-utils";
import { formatCurrency } from "utils/format-utils";

import type { Lien } from "../../pages/liens/LienTypes";
import { ControlledDateInput, ControlledInput } from "../Card/ControlledInput";
import InvoiceLineItems from "./InvoiceLineItems";
import type {
  InvoiceableEntity,
  InvoiceContainerConfig,
  InvoiceType,
} from "./InvoiceTypes";

type InvoiceFormValues = {
  date_posted: Date | null;
  due_date: Date | null;
  subject: string;
  billing_email_address: string;
  service: string;
  description: string;
  status: string;
  liens: { value: number; label: string }[];
  line_items: {
    id: number;
    rate: string;
    quantity: string;
    description: string;
  }[];
};

type InvoiceDetailFormProps = {
  invoice: InvoiceType;
  entity: InvoiceableEntity;
  config?: InvoiceContainerConfig;
  availableLiens?: Lien[]; // array of liens available for selection
};

const formatInvoiceDate = (dateString: string | null) => {
  if (!dateString) return "Invoice Details";
  const date = parse(dateString, DATE_FORMAT, new Date());
  return `Invoice Details - ${format(date, "MM/dd/yyyy")}`;
};

const InvoiceDetailForm = ({
  invoice,
  entity,
  config,
  availableLiens = [],
}: InvoiceDetailFormProps) => {
  const {
    updateInvoice,
    finalizeInvoice,
    markAsManuallySent,
    unmarkAsManuallySent,
  } = useInvoice();
  const { isConnectionValid, isLoading } = useQuickBooksConnectionStatus();
  const { startReconnection } = useQuickBooksReconnect();

  // fetch client data to grab billing email
  const { data: client } = useGetClientByIdQuery(
    { id: entity.client.id.toString() },
    { skip: !entity.client.id },
  );

  const isReadOnly =
    invoice.status === "Finalized" ||
    invoice.status === "Paid" ||
    invoice.status === "Voided";
  const hasLineItems = invoice.line_items.length > 0;

  // local state for optimistic updates of the email toggle
  const [emailToggleState, setEmailToggleState] = useState<boolean>(
    invoice.email_to_customer_when_finalized || false,
  );

  // local state for optimistic updates of the CMS toggle
  const [cmsToggleState, setCmsToggleState] = useState<boolean>(
    invoice.is_cms_invoice || false,
  );

  // local state for optimistic updates of the delivery status toggle
  const [deliveryStatusToggleState, setDeliveryStatusToggleState] =
    useState<boolean>(invoice.delivery_status === "Sent Manually");

  const {
    control,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<InvoiceFormValues>();

  // watch the due_date field to check if it's set
  const formDueDate = watch("due_date");
  const hasDueDate = !!(invoice.due_date || formDueDate);

  // watch the billing_email_address field to check if it's set
  const formBillingEmail = watch("billing_email_address");
  const hasBillingEmail = !!(
    invoice.billing_email_address ||
    formBillingEmail ||
    client?.billing_email
  );

  // convert liens to options for the multi-select
  const lienOptions = useMemo(() => {
    if (!availableLiens?.length) return [];

    return availableLiens.map((lien) => ({
      value: lien.id,
      label: `${lien.part_type} - Case ${lien.case_number || ""}`,
    }));
  }, [availableLiens]);

  // convert invoice.liens to the format expected by the multi-select
  const selectedLienOptions = useMemo(() => {
    if (!invoice?.liens?.length) return [];

    return invoice.liens.map((lienId) => {
      const lien = availableLiens.find((l) => l.id === lienId);
      return {
        value: lienId,
        label: lien
          ? `${lien.part_type} - Case ${lien.case_number || ""}`
          : `Lien ${lienId}`,
      };
    });
  }, [invoice.liens, availableLiens]);

  const handleFieldUpdate = useCallback(
    async (fieldName: string, value: string | number | number[] | boolean) => {
      await updateInvoice(invoice.id, { [fieldName]: value });
    },
    [invoice.id, updateInvoice],
  );

  const handleLiensUpdate = useCallback(
    async (options: { value: number; label: string }[]) => {
      const lienIds = options.map((option) => option.value);
      await updateInvoice(invoice.id, { liens: lienIds });
    },
    [invoice.id, updateInvoice],
  );

  const handleEmailToggleUpdate = useCallback(
    async (checked: boolean) => {
      // optimistic update - update UI immediately
      setEmailToggleState(checked);

      try {
        await updateInvoice(invoice.id, {
          email_to_customer_when_finalized: checked,
        });
      } catch (error) {
        // if the update fails, revert the optimistic update
        console.error("Failed to update email toggle:", error);
        setEmailToggleState(!checked);
      }
    },
    [invoice.id, updateInvoice],
  );

  const handleCmsToggleUpdate = useCallback(
    async (checked: boolean) => {
      // optimistic update - update UI immediately
      setCmsToggleState(checked);

      try {
        await updateInvoice(invoice.id, {
          is_cms_invoice: checked,
        });
      } catch (error) {
        // if the update fails, revert the optimistic update
        console.error("Failed to update CMS toggle:", error);
        setCmsToggleState(!checked);
      }
    },
    [invoice.id, updateInvoice],
  );

  const handleDeliveryStatusToggleUpdate = useCallback(
    async (checked: boolean) => {
      // optimistic update - update UI immediately
      setDeliveryStatusToggleState(checked);

      try {
        if (checked) {
          await markAsManuallySent(invoice.id);
        } else {
          await unmarkAsManuallySent(invoice.id);
        }
      } catch (error) {
        // if the update fails, revert the optimistic update
        console.error("Failed to update delivery status toggle:", error);
        setDeliveryStatusToggleState(!checked);
      }
    },
    [invoice.id, markAsManuallySent, unmarkAsManuallySent],
  );

  useEffect(() => {
    if (!invoice) return;

    // auto-populate billing_email_address from client if not set on invoice
    const billingEmailAddress =
      invoice.billing_email_address || client?.billing_email || "";

    // for Draft invoices, set due_date to 30 days from now instead of the stored value
    let dueDateToUse: Date | null = null;
    if (invoice.status === "Draft") {
      // always set to 30 days from now for Draft invoices
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      dueDateToUse = thirtyDaysFromNow;
    } else if (invoice.due_date) {
      // for non-Draft invoices, use the stored due_date
      dueDateToUse = parse(invoice.due_date, DATE_FORMAT, new Date());
    }

    reset(
      {
        date_posted: invoice.date_posted
          ? parse(invoice.date_posted, DATE_FORMAT, new Date())
          : null,
        due_date: dueDateToUse,
        subject: invoice.email_subject,
        billing_email_address: billingEmailAddress,
        service: invoice.service_type || "N/A",
        description: invoice.description,
        status: invoice.status,
        liens: selectedLienOptions,
        line_items: invoice.line_items.map((item) => ({
          id: item.id,
          rate: item.rate.toString(),
          quantity: item.quantity.toString(),
          description: item.description || "---",
        })),
      },
      { keepDefaultValues: true },
    );
  }, [invoice, reset, selectedLienOptions, client?.billing_email]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  // auto-populate and save billing email from client if not set on invoice
  useEffect(() => {
    if (!invoice?.id || !client?.billing_email) return;

    // only auto-populate if invoice doesn't have a billing email set
    if (!invoice.billing_email_address) {
      handleFieldUpdate("billing_email_address", client.billing_email);
    }
  }, [
    invoice?.id,
    invoice?.billing_email_address,
    client?.billing_email,
    handleFieldUpdate,
  ]);

  // sync local email toggle state when invoice changes
  useEffect(() => {
    setEmailToggleState(invoice.email_to_customer_when_finalized || false);
  }, [invoice.email_to_customer_when_finalized]);

  // sync local CMS toggle state when invoice changes
  useEffect(() => {
    setCmsToggleState(invoice.is_cms_invoice || false);
  }, [invoice.is_cms_invoice]);

  // sync local delivery status toggle state when invoice changes
  useEffect(() => {
    setDeliveryStatusToggleState(invoice.delivery_status === "Sent Manually");
  }, [invoice.delivery_status]);

  const [isFinalizing, setIsFinalizing] = useState(false);

  const handleFinalize = useCallback(async () => {
    if (!invoice?.id) return;
    // Block re-entry to prevent duplicate QuickBooks invoices on rapid clicks.
    if (isFinalizing) return;

    setIsFinalizing(true);

    const formValues = getValues();
    const invoiceData: Partial<InvoiceType> = {};

    // only include due_date if it's set in the form
    if (formValues.due_date) {
      invoiceData.due_date = format(formValues.due_date, DATE_FORMAT);
    }

    try {
      // finalize with the current form data
      await finalizeInvoice(invoice.id, invoiceData);
    } finally {
      setIsFinalizing(false);
    }
  }, [getValues, invoice?.id, finalizeInvoice, isFinalizing]);

  if (!invoice) {
    console.warn("InvoiceDetailForm: invoice is undefined");
    return null;
  }

  if (!entity) {
    console.warn("InvoiceDetailForm: entity is undefined");
    return null;
  }

  // only show the liens multi-select for lien invoices
  const showLiensSelect = entity.type === SERVICE_TYPE.LIEN;

  const getFinalizeValidation = () => {
    if (!hasDueDate) {
      return {
        isValid: false,
        tooltipContent: "Set a due date to finalize this invoice",
      };
    }
    if (!hasLineItems) {
      return {
        isValid: false,
        tooltipContent: "Add at least one line item to finalize this invoice",
      };
    }

    // check if all line items have both date and assignee
    const incompleteLineItems = invoice.line_items.filter(
      (item) => !item.date || !item.assignee,
    );
    if (incompleteLineItems.length > 0) {
      return {
        isValid: false,
        tooltipContent:
          "Date of service and assignee are required on all invoice line items before an invoice can be finalized",
      };
    }

    if (!hasBillingEmail) {
      return {
        isValid: false,
        tooltipContent:
          "Set a valid billing email for the client or enter one on this invoice",
      };
    }
    return {
      isValid: true,
      tooltipContent: "",
    };
  };

  const { isValid: canFinalize, tooltipContent } = getFinalizeValidation();

  return (
    <div className={`card form audit-invoice-card`}>
      <div className="card__header">
        {formatInvoiceDate(invoice.date_posted)}
      </div>
      <div className="card__children">
        {showLiensSelect && (
          <>
            <ControlledMultiSelect
              control={control}
              name="liens"
              label="Liens to Invoice"
              errors={errors}
              options={lienOptions}
              onSave={handleLiensUpdate}
              disableRemoveCondition={false}
              readOnly={
                !invoice.id || isReadOnly || availableLiens?.length === 0
              }
              required={false}
            />
            <SectionBreak />
          </>
        )}
        <ControlledInput
          control={control}
          name="status"
          label="Invoice Status"
          errors={errors}
          componentType={TextInput}
          readOnly={true}
        />
        <ControlledDateInput
          control={control}
          name="due_date"
          label="Due Date"
          allowFutureDates={true}
          errors={errors}
          onSave={(value) => handleFieldUpdate("due_date", value)}
          readOnly={!invoice.id || isReadOnly}
          isClearable={!invoice.due_date}
        />
        <ControlledInput
          control={control}
          name="subject"
          label="Client Subject"
          errors={errors}
          onSave={(value) => handleFieldUpdate("email_subject", value)}
          componentType={TextInput}
          maxLength={40}
          readOnly={!invoice.id || isReadOnly}
        />
        <ControlledInput
          control={control}
          name="billing_email_address"
          label="Billing Email"
          errors={errors}
          onSave={(value) => handleFieldUpdate("billing_email_address", value)}
          componentType={TextInput}
          readOnly={!invoice.id || isReadOnly}
        />
        {(invoice.status === "Draft" ||
          invoice.status === "Finalized" ||
          invoice.status === "Paid" ||
          invoice.status === "Voided") && (
          <Toggle
            checked={emailToggleState}
            onChange={
              isReadOnly || !hasBillingEmail
                ? () => {}
                : handleEmailToggleUpdate
            }
            leftLabel={"QB Auto-send"}
            disabled={!hasBillingEmail}
            tooltipMessage={
              !hasBillingEmail
                ? "Set a client billing email or enter an email on this invoice to enable"
                : undefined
            }
          />
        )}
        {config?.showCMS && (
          <Toggle
            checked={cmsToggleState}
            onChange={isReadOnly ? () => {} : handleCmsToggleUpdate}
            leftLabel="CMS Invoice"
            disabled={isReadOnly}
          />
        )}
        <Toggle
          checked={deliveryStatusToggleState}
          onChange={handleDeliveryStatusToggleUpdate}
          leftLabel="Sent Manually to Customer"
          disabled={invoice.status === "Draft"}
          tooltipMessage={
            invoice.status === "Draft"
              ? "Invoice must be finalized before marking as manually sent"
              : undefined
          }
        />
        <SectionBreak />
        {invoice.id && (
          <>
            <InvoiceLineItems invoice={invoice} isFinalized={isReadOnly} />
            <SectionBreak />
          </>
        )}
        <ControlledInput
          control={control}
          name="service"
          label="Service"
          errors={errors}
          onSave={(value) => handleFieldUpdate("service", value)}
          componentType={TextInput}
          maxLength={40}
          readOnly={true}
        />
        <SectionBreak />
        <ControlledTextArea
          name="description"
          control={control}
          errors={errors}
          label="Description (For Internal Use Only)"
          advancedEdit={false}
          onSave={(value) => handleFieldUpdate("description", value)}
          readOnly={!invoice.id || isReadOnly}
        />
        <div className="FutureMedicalInvoiceDetailForm__Amount">
          <div className="amount-row">
            <label className="amount-label">Total Amount Due:</label>
            <figure>
              <b>{formatCurrency(invoice.total)}</b>
            </figure>
          </div>
          {invoice.status === "Draft" &&
            (isLoading ? (
              <Button className="finalize-button" onClick={() => {}} disabled>
                Connecting...
              </Button>
            ) : !isConnectionValid ? (
              <Tooltip
                content={
                  !isConnectionValid
                    ? "QuickBooks connection is expired. Click to reconnect."
                    : ""
                }
                className="finalize-tooltip-container"
              >
                <Button
                  className="reconnect-button"
                  onClick={() => startReconnection()}
                >
                  Reconnect QuickBooks
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                content={tooltipContent}
                className="finalize-tooltip-container"
              >
                <Button
                  onClick={handleFinalize}
                  className="finalize-button"
                  disabled={!canFinalize || isFinalizing}
                >
                  Finalize
                </Button>
              </Tooltip>
            ))}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailForm;
